

















































































































































import LazyHydrate from "vue-lazy-hydration";
import { SfSelect, SfHeading, SfNotification, SfButton } from "@storefront-ui/vue";
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  ssrRef,
  useFetch,
} from "@nuxtjs/composition-api";
import { CacheTagPrefix, useCache } from "@vue-storefront/cache";
import { usePageStore } from "~/stores/page";
import SkeletonLoader from "~/components/SkeletonLoader/index.vue";
import CategoryPagination from "~/modules/catalog/category/components/pagination/CategoryPagination.vue";
import {
  useCart,
  useCategory,
  useFacet,
  useUiHelpers,
  useUiState,
} from "~/composables";

import { useAddToCart } from "~/helpers/cart/addToCart";
import { useWishlist } from "~/modules/wishlist/composables/useWishlist";
import { usePrice } from "~/modules/catalog/pricing/usePrice";
import { useCategoryContent } from "~/modules/catalog/category/components/cms/useCategoryContent";
import { useTraverseCategory } from "~/modules/catalog/category/helpers/useTraverseCategory";
import facetGetters from "~/modules/catalog/category/getters/facetGetters";
import { getMetaInfo } from "~/helpers/getMetaInfo";

import CategoryBreadcrumbs from "~/modules/catalog/category/components/breadcrumbs/CategoryBreadcrumbs.vue";

import type {
  CategoryTree,
  Maybe,
  ProductInterface,
} from "~/modules/GraphQL/types";
import type { SortingModel } from "~/modules/catalog/category/composables/useFacet/sortingOptions";
import type { Pagination } from "~/composables/types";
import type { Product } from "~/modules/catalog/product/types";
import categoryProduct from "~/assets/category.json";
import {
  CatalogID,
  SubcategoryMetalworking,
  SubcategoryAbrasives,
  CategoryName,
  CategoryNameChange,
} from "../category/CategoryTypeEnum";
import _ from "lodash";
export default defineComponent({
  name: "CategoryPage",
  components: {
    CategoryPagination,
    CategoryFilters: () =>
      import(
        "~/modules/catalog/category/components/filters/CategoryFilters.vue"
      ),
    CategoryProductGrid: () =>
      import(
        "~/modules/catalog/category/components/views/CategoryProductGrid.vue"
      ),
    QuickOrder: () =>
      import("~/modules/catalog/category/components/quickorder/QuickOrder.vue"),
    CategoryBreadcrumbs,
    SfSelect,
    LazyHydrate,
    SfHeading,
    SkeletonLoader,
    ProcessLoading: () =>
      import(
        "~/modules/checkout/pages/Checkout/processPayment/ProcessLoading.vue"
      ),
    SfNotification,
    SfButton
  },
  transition: "fade",
  setup() {
    const { routeData } = usePageStore();
    const { getContentData } = useCategoryContent();
    const { loadCategoryMeta } = useCategory();
    const { addTags } = useCache();
    const uiHelpers = useUiHelpers();
    const isShowCms = ref(false);
    const isShowProducts = ref(false);
    const checkCategory = ref(false);
    let loadingProcess = ref(false);
    const products = ssrRef<ProductInterface[]>([]);
    const sortBy = ref<SortingModel>({ selected: "", options: [] });
    const pagination = ref<Pagination>({});

    const checkCatalog = Object.values(CatalogID).includes(routeData.uid);

    const productContainerElement = ref<HTMLElement | null>(null);
    const message = ref("");
    const isRemoveModalVisible = ref(false);
    const title = ref("");
    const {
      toggleFilterSidebar,
      changeToCategoryListView,
      changeToCategoryGridView,
      isCategoryGridView,
      isFilterSidebarOpen,
    } = useUiState();
    const {
      load: loadWishlist,
      addItem: addItemToWishlistBase,
      isInWishlist,
      removeItem: removeItemFromWishlist,
    } = useWishlist();
    const { result, search } = useFacet();
    const { checkCartAvailable } = useCart();
    const { addItemToCart } = useAddToCart();
    const addItemToCartProduct = async (event) => {
      loadingProcess.value = true;
      const result = await checkCartAvailable(
        event.product.saleable[0].qty,
        event.quantity,
        event.product.sku
      );
      isRemoveModalVisible.value = result.isRemoveModalVisible;
      message.value = result.message;
      if (!isRemoveModalVisible.value) addItemToCart(event, loadingProcess);
      loadingProcess.value = false;
    };
    const categoryMeta = ref(null);

    const addItemToWishlist = async (product: Product) => {
      await (isInWishlist({ product })
        ? removeItemFromWishlist({ product })
        : addItemToWishlistBase({ product }));
    };

    const {
      categoryTree,
      activeCategory,
      loadCategoryTree,
    } = useTraverseCategory();

    const activeCategoryName = computed(() => {
      const nameCategory = activeCategory.value?.name ?? "";
      switch (nameCategory.toLowerCase().trim()) {
        case CategoryName.END_MILLS.toLowerCase().trim():
          return CategoryNameChange.END_MILLS_CHANGE;
        case CategoryName.TURING_TOOL_HOLDERS.toLowerCase().trim():
          return CategoryNameChange.TURING_TOOL_HOLDERS_CHANGE;
        default:
          break;
      }
      return activeCategory.value?.name ?? "";
    });
    const categoryUid = checkCatalog ? "" : routeData.uid;
    const categoryItems = ref<Array<Maybe<CategoryTree>>>([]);

    const { fetch } = useFetch(async () => {
      if (!activeCategory.value) {
        await loadCategoryTree();
      }
      if (checkCatalog) {
        if (Object.values(SubcategoryAbrasives).includes(routeData.uid)) {
          categoryItems.value = categoryTree.value.children
            .filter((item) => item.uid == CatalogID.METALWORKING)[0]
            .children.filter(
              (item) => item.uid == CatalogID.ABRASIVES_DIAMOND_TOOLS
            )[0]
            .children.filter(
              (subItem) => subItem.uid == routeData.uid
            )[0].children;
        } else if (
          Object.values(SubcategoryMetalworking).includes(routeData.uid)
        ) {
          categoryItems.value = categoryTree.value.children
            .filter((item) => item.uid == CatalogID.METALWORKING)[0]
            .children.filter(
              (subItem) => subItem.uid == routeData.uid
            )[0].children;
        } else {
          categoryItems.value = categoryTree.value.children.filter(
            (item) => item.uid == routeData.uid
          )[0].children;
        }
      }
      const skus: string[] = checkCatalog
        ? categoryProduct[routeData.uid]["skus"]
        : [];
      const filters = _.assign(
        {
          sku: skus,
          category_uid: [categoryUid],
        },
        uiHelpers.getFacetsFromURL().filters
      );
      const [categoryMetaData] = await Promise.all([
        loadCategoryMeta({ category_uid: routeData.uid }),
        search({
          ...uiHelpers.getFacetsFromURL(),
          filters: filters,
        }),
      ]);

      categoryMeta.value = categoryMetaData;
      products.value = facetGetters.getProducts(result.value) ?? [];
      sortBy.value = facetGetters.getSortOptions(result.value);
      pagination.value = facetGetters.getPagination(result.value);
      const tags = [{ prefix: CacheTagPrefix.View, value: routeData.uid }];
      addTags([...tags]);
    });

    const isPriceLoaded = ref(false);

    onMounted(async () => {
      if (!checkCatalog) {
        const { getPricesBySku } = usePrice();
        if (products.value.length > 0) {
          const skus = products.value.map((item) => {
            return item.sku;
          });
          const priceData = await getPricesBySku(
            skus,
            pagination.value.itemsPerPage
          );
          products.value = products.value.map((product) => {
            const priceRange = priceData.items.find(
              (item) => item.sku === product.sku
            )?.price_range;

            if (priceRange) {
              return {
                ...product,
                price_range: priceRange,
              };
            }

            return { ...product };
          });
        }
      }

      isPriceLoaded.value = true;
      categoryItems.value.sort((a, b) => a.name.localeCompare(b.name));
    });

    const goToPage = (page: number) => {
      uiHelpers.changePage(page, false);
      fetch();
    };

    const doChangeItemsPerPage = (itemsPerPage: number) => {
      uiHelpers.changeItemsPerPage(itemsPerPage, false);
      goToPage(0);
    };

    const onReloadProducts = () => {
      checkCategory.value = true;
      goToPage(0);
      productContainerElement.value.scrollIntoView();
    };

    return {
      isPriceLoaded,
      ...uiHelpers,
      toggleFilterSidebar,
      isCategoryGridView,
      changeToCategoryListView,
      changeToCategoryGridView,
      isFilterSidebarOpen,
      addItemToCart,
      addItemToWishlist,
      pagination,
      products,
      sortBy,
      isShowCms,
      isShowProducts,
      activeCategoryName,
      routeData,
      doChangeItemsPerPage,
      productContainerElement,
      categoryMeta,
      onReloadProducts,
      goToPage,
      checkCategory,
      checkCatalog,
      categoryItems,
      addItemToCartProduct,
      loadingProcess,
      title,
      isRemoveModalVisible,
      message,
    };
  },
  head() {
    return getMetaInfo(this.categoryMeta, true);
  },
});
